import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, LocateFixed, MapPin, Search, Ticket, ArrowUpRight } from 'lucide-react';
import { api, fmt } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { buildServiceTilesForRole, recordTileOpen } from '../lib/serviceTiles';
import './HomeDiscovery.css';

const SERVICE_PAGES = {
  '/app/gift-cards': lazy(() => import('../pages/GiftCards')),
  '/app/tickets': lazy(() => import('../pages/Tickets')),
  '/app/transport': lazy(() => import('../pages/TransportHub')),
  '/app/vendors': lazy(() => import('../pages/VendorsPages').then(m => ({ default: m.VendorsList }))),
  '/app/printhub': lazy(() => import('../pages/PrintHub')),
  '/app/idelivery': lazy(() => import('../pages/IDelivery')),
  '/app/find-my-bed': lazy(() => import('../pages/FindMyBed')),
  '/app/services': lazy(() => import('../pages/ServiceProviders')),
  '/app/smart-queues': lazy(() => import('../pages/SmartQueues')),
  '/app/my-queues': lazy(() => import('../pages/MyQueues')),
  '/app/directory': lazy(() => import('../pages/PartnersDirectory')),
  '/app/donations': lazy(() => import('../pages/Donations')),
  '/app/connect': lazy(() => import('../pages/Connect')),
  '/app/loyalty': lazy(() => import('../pages/Loyalty')),
  '/app/spin': lazy(() => import('../pages/Spin')),
  '/app/refunds': lazy(() => import('../pages/Refunds')),
};

function EventCard({ event }) {
  const target = `/app/events/${encodeURIComponent(event.id)}`;
  const date = new Date(event.event_datetime);
  const validDate = !Number.isNaN(date.getTime());
  const soldOut = event.status === 'sold_out' || event.availability === 'sold_out';
  return <article className="home-event-card" data-testid={`home-event-${event.id}`}>
    <Link to={target} className="home-event-poster" aria-label={`View ${event.name}`}>
      {event.media_url ? event.media_type === 'video'
        ? <video src={event.media_url} preload="metadata" muted playsInline aria-label={event.name} />
        : <img src={event.media_url} alt={event.name} loading="lazy" />
        : <div className="home-event-placeholder"><Ticket size={42} aria-hidden="true"/><span>{event.name}</span></div>}
      {validDate && <span className="home-event-date"><b>{date.getDate()}</b>{date.toLocaleDateString(undefined, {month:'short'})}</span>}
      {event.age_restriction > 0 && <span className="home-event-age">{event.age_restriction}+</span>}
    </Link>
    <div className="home-event-copy">
      <span className="home-discovery-eyebrow">{event.category || 'Event'}</span>
      <h3><Link to={target}>{event.name}</Link></h3>
      <p><MapPin size={14} aria-hidden="true"/>{event.venue || 'Venue to be announced'}{Number.isFinite(Number(event.distance_km)) && <> · about {event.distance_km} km away</>}</p>
      <p><Calendar size={14} aria-hidden="true"/>{validDate ? date.toLocaleString(undefined, {dateStyle:'medium',timeStyle:'short'}) : 'Date to be announced'}</p>
      <div className="home-event-bottom"><strong>{event.ticket_tiers?.length > 0 ? 'From ' : ''}{Number(event.ticket_price) > 0 ? fmt(event.ticket_price) : 'Free'}</strong>
        {soldOut ? <span className="home-event-sold-out">Sold out</span> : <Link className="home-event-buy" to={`${target}?purchase=1#ticket-purchase`} aria-label={`Buy tickets for ${event.name}`}>Buy Now <ArrowUpRight size={15} aria-hidden="true"/></Link>}
      </div>
    </div>
  </article>;
}

function HomeEvents() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All events');
  const [retry, setRetry] = useState(0);
  const [coords, setCoords] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [locationMessage, setLocationMessage] = useState('');
  const eventsTrack = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let sequence = 0;
    const load = async () => {
      const request = ++sequence;
      try {
        const {data} = await api.get('/events', { params: coords || {} });
        if (!cancelled && request === sequence) { setEvents(Array.isArray(data) ? data : []); setStatus('ready'); }
      } catch (_) { if (!cancelled && request === sequence) setStatus('error'); }
    };
    load();
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    const timer = setInterval(visible, 30000);
    window.addEventListener('focus', visible);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener('focus', visible); };
  }, [retry, coords]);
  const findNearby = () => {
    if (!navigator.geolocation) { setLocationStatus('error'); setLocationMessage('Location is not available in this browser.'); return; }
    setLocationStatus('loading'); setLocationMessage('');
    navigator.geolocation.getCurrentPosition(({coords: point}) => {
      setCoords({lat: point.latitude, lng: point.longitude});
      setLocationStatus('active'); setLocationMessage('Showing events nearest to you first.');
    }, (error) => {
      setLocationStatus('error');
      setLocationMessage(error.code === error.PERMISSION_DENIED ? 'Allow location access in your browser to find nearby events.' : 'Could not get your location. Please try again.');
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
  };
  const categories = [...new Set(events.map(event => event.category).filter(Boolean))];
  const activeCategory = categories.includes(category) ? category : 'All events';
  const search = query.trim().toLocaleLowerCase();
  const filtered = events.filter(event => (activeCategory === 'All events' || event.category === activeCategory) &&
    [event.name, event.venue, event.category].some(value => String(value || '').toLocaleLowerCase().includes(search)));
  return <section aria-labelledby="home-events-title" data-testid="home-events">
    <div className="home-events-heading"><div><span className="home-discovery-eyebrow">Discover • Book • Enjoy</span><h2 id="home-events-title">Your next great experience.</h2><p>Explore events and find your next ticket.</p></div>
      <div className="home-event-filters">
        <label className="home-event-search"><Search size={18} aria-hidden="true"/><input type="search" aria-label="Search event tickets" placeholder="Event, venue or category" value={query} onChange={e => setQuery(e.target.value)}/></label>
        <button className={`home-event-location${coords ? ' is-active' : ''}`} type="button" onClick={findNearby} disabled={locationStatus === 'loading'} aria-label="Find events near me">
          <LocateFixed size={17} aria-hidden="true"/>{locationStatus === 'loading' ? 'Finding…' : coords ? 'Near me' : 'Near me'}
        </button>
      </div>
    </div>
    {locationMessage && <p className={`home-event-location-note${locationStatus === 'error' ? ' is-error' : ''}`} role={locationStatus === 'error' ? 'alert' : 'status'}>{locationMessage}</p>}
    {locationStatus === 'active' && <p className="home-event-location-note" role="status">Nearby events are shown first. Your exact location stays on this device.</p>}
    {categories.length > 0 && <div className="home-event-categories" aria-label="Event categories">{['All events', ...categories].map(value => <button key={value} type="button" aria-pressed={activeCategory === value} onClick={() => setCategory(value)}>{value}</button>)}</div>}
    {status === 'loading' ? <div className="home-discovery-empty" role="status">Finding upcoming events…</div>
      : status === 'error' ? <div className="home-discovery-empty" role="alert">We couldn’t load events. <button type="button" onClick={() => { setStatus('loading'); setRetry(value => value + 1); }}>Try again</button></div>
      : filtered.length ? <div className="home-events-carousel-wrap">
        {filtered.length > 2 && <div className="home-events-controls"><span>Swipe to explore · {filtered.length} events</span><div><button type="button" aria-label="Previous events" onClick={() => eventsTrack.current?.scrollBy({left: -eventsTrack.current.clientWidth, behavior: 'smooth'})}><ChevronLeft size={18}/></button><button type="button" aria-label="Next events" onClick={() => eventsTrack.current?.scrollBy({left: eventsTrack.current.clientWidth, behavior: 'smooth'})}><ChevronRight size={18}/></button></div></div>}
        <div className="home-events-grid" ref={eventsTrack} aria-label="Upcoming events">{filtered.map(event => <EventCard key={event.id} event={event}/>)}</div>
      </div>
      : <div className="home-discovery-empty"><Ticket size={30} aria-hidden="true"/><h3>{query || activeCategory !== 'All events' ? 'No matching events' : 'Your next experience is on its way'}</h3><p>{query || activeCategory !== 'All events' ? 'Try another search or category.' : 'Tickets will appear here when organizers publish their events.'}</p></div>}
  </section>;
}

export default function HomeDiscovery({ children }) {
  const {user} = useAuth();
  const {isEnabled, ready} = useFeatureFlags();
  const services = buildServiceTilesForRole(user).filter(tile => isEnabled(tile.flag));
  const [selection, setSelection] = useState('/app/events');
  // Re-resolve each render so disabling a service immediately unmounts its page.
  const selected = services.find(tile => tile.to === selection) || services.find(tile => tile.to === '/app/events') || services[0];
  const Page = selected && SERVICE_PAGES[selected.to];
  return <section className="home-discovery" data-testid="home-discovery">
    {children && <div className="mb-6 space-y-3" data-testid="home-membership">{children}</div>}
    <nav className="home-service-menu" aria-label="Services"><div><span className="home-discovery-eyebrow">Explore iCaard</span><h2>What are you looking for?</h2></div>
      <label><span>Choose a service</span><select aria-label="Choose a service" value={selected?.to || ''} disabled={!ready || !services.length} onChange={event => {setSelection(event.target.value); recordTileOpen(event.target.value);}}>
        {!services.length && <option value="">{ready ? 'No services available' : 'Loading services…'}</option>}
        {services.map(tile => <option key={tile.to} value={tile.to}>{tile.label}</option>)}
      </select></label>
    </nav>
    {selected?.to === '/app/events' ? <HomeEvents /> : selected ? <div className="home-selected-service" key={selected.to}>
      <Suspense fallback={<div className="home-discovery-empty" role="status">Loading {selected.label}…</div>}>
        {Page ? <Page/> : <div className="home-discovery-empty"><h2>{selected.label}</h2><p>{selected.desc}</p><Link to={selected.to}>Open {selected.label} <ArrowUpRight size={16}/></Link></div>}
      </Suspense></div> : ready ? <div className="home-discovery-empty">Services are being prepared. Please check back soon.</div> : <div role="status">Loading services…</div>}
  </section>;
}
